'use client';

import type { CSSProperties } from 'react';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { percentFracPart, percentIntPart } from '@/lib/format/format-number';

/** Whole-percent label (`20%`). A string format → `AnimatedNumber`'s fast path. */
function formatOutcomePercent(v: number): string {
  return `${String(Math.round(v))}%`;
}

/**
 * Outcome percentage label — shared by the markets feed card
 * (`market-row-card.tsx`) and the featured-hero outcome cards
 * (`outcome-sparkline-card.tsx`).
 *
 * `decimals` (set when two outcomes round to the same whole percent) renders
 * 2-decimal precision: the integer is the hero, the fractional digits + `%`
 * form a small `.NN%` suffix a type-step down — so near-tied outcomes stay
 * distinguishable.
 *
 * PERF — the suffix is its OWN string-format `AnimatedNumber` inside a static
 * styled `<span>`, NOT a single JSX `format`. A JSX `format` forces
 * `AnimatedNumber` into a per-animation-frame React `setState` (~48 renders
 * per 800 ms tween — measured 1358 renders/s across the feed); two string
 * formats both take `AnimatedNumber`'s zero-React-render DOM-write fast path.
 * Both numbers animate the same `value` with identical duration/easing, so
 * they tick in lockstep.
 *
 * DOM — accepts `className` / `style` / `data-testid` so the typography
 * wrapper that callers used to add can be dropped. In the no-decimals case
 * these forward straight onto the single `AnimatedNumber`'s `<span>`,
 * saving one element per percentage label. In the decimals case they apply
 * to the wrapper `<span>` that contains the integer + suffix.
 */
export function AnimatedOutcomePercent(props: {
  value: number;
  decimals: boolean;
  /** Tick up from 0 on mount. The feed card animates in; the hero snaps. */
  animateOnMount?: boolean;
  /** Typography / colour classes applied to the rendered span. */
  className?: string;
  /** Dynamic style — used by sparkline-card for per-outcome colour vars. */
  style?: CSSProperties;
  /** Forwarded to the rendered span (no-decimals) or wrapper span (decimals). */
  'data-testid'?: string;
}): React.ReactNode {
  const { value, decimals, animateOnMount = false, className, style, ...rest } = props;
  const testId = rest['data-testid'];
  if (!decimals) {
    return (
      <AnimatedNumber
        value={value}
        format={formatOutcomePercent}
        animateOnMount={animateOnMount}
        {...(className !== undefined ? { className } : {})}
        {...(style !== undefined ? { style } : {})}
        {...(testId !== undefined ? { 'data-testid': testId } : {})}
      />
    );
  }
  return (
    <span
      {...(className !== undefined ? { className } : {})}
      {...(style !== undefined ? { style } : {})}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      <AnimatedNumber value={value} format={percentIntPart} animateOnMount={animateOnMount} />
      <span className="text-caption">
        <AnimatedNumber value={value} format={percentFracPart} animateOnMount={animateOnMount} />
        <span className="ml-0.5">%</span>
      </span>
    </span>
  );
}
